import { initLazyImages } from '../utils/lazy-images.js';
import { initLazyScript } from '../utils/theme-functions.js';

class InfinteCollection extends HTMLElement {
    constructor() {
        super();

        initLazyScript(this, this.init.bind(this));

        this.productCardSelector = '.card--product, .card--variant';

        //listen to loading from cache event
        document.addEventListener('on:bfcache:load-restore', this.reload.bind(this));
    }

    disconnectedCallback() {
        this.destroy();
    }

    init() {
        this.pagination = this.querySelector('.js-pagination');
        if (!this.pagination) return;
        this.nextPageLink = this.pagination.querySelector('[data-next-page-link]');
        this.results = this.querySelectorAll(this.productCardSelector);
        this.controller = null;

        // Bind click handlers to pagination results
        this.resultClickHandler = this.resultClickHandler || this.handleResultClick.bind(this);
        this.addEventListener('click', this.resultClickHandler);

        if (this.nextPageLink && this.results.length > 0) {
            this.initObserver();
        } else {
            console.error('No results found', this.nextPageLink, this.results);
        }
    }

    initObserver() {
        this.paginationObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        this.loadMore();

                        // Continue to load results as long as the 'load more' button is visible
                        this.intersectingTimer = setInterval(this.loadMore.bind(this), 1000);
                    } else if (this.intersectingTimer) {
                        clearInterval(this.intersectingTimer);
                    }
                });
            },
            { rootMargin: `${window.innerHeight * 1.5}px 0px` }
        );

        this.paginationObserver.observe(this.pagination);
    }

    /**
     * Destroys the component
     */
    destroy() {
        if (this.intersectingTimer) clearInterval(this.intersectingTimer);
        if (this.paginationObserver) this.paginationObserver.disconnect();
        this.removeEventListener('click', this.resultClickHandler);
    }

    /**
     * Reloads the component
     */
    reload() {
        this.destroy();
        this.init();
    }

    /**
     * Handles a click on a item in the pagination
     * @param {object} evt - Event object
     */
    handleResultClick(evt) {
        if (evt.target.closest('a') && evt.target.closest(this.productCardSelector) && this.controller) {
            this.controller.abort();
        }
    }

    /**
     * Fetches more results from the next page of results, and dynamically updates this page
     */
    async loadMore() {
        if (!this.isFetching && this.dataset.pauseInfiniteScroll !== 'true') {
            this.isFetching = true;
            this.controller = new AbortController();

            try {
                if (!this.nextPageLink) throw new Error("No 'Load more' button found.");

                const response = await fetch(this.nextPageLink.href, {
                    signal: this.controller.signal
                });

                if (!response.ok) throw new Error(response.status);
                if (this.dataset.pauseInfiniteScroll === 'true') {
                    throw new Error('Infinite scroll was interrupted.');
                }

                const html = await response.text();

                this.updateElements(html);
            } catch (error) {
                console.log('Unable to fetch next page of results', error);
                if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            } finally {
                this.controller = null;
                this.isFetching = false;
            }
        }
    }

    updateElements(html) {
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;

        let newResultsHtml = '';
        const lastResult = this.results[this.results.length - 1];
        tmpl.content.querySelectorAll(this.productCardSelector).forEach((result) => {
            newResultsHtml += result.outerHTML;
        });
        lastResult.insertAdjacentHTML('afterend', newResultsHtml);

        initLazyImages();

        this.results = this.querySelectorAll(this.productCardSelector);

        const newPagination = tmpl.content.querySelector('.js-pagination');
        this.pagination.innerHTML = newPagination.innerHTML;
        this.nextPageLink = this.pagination.querySelector('[data-next-page-link]');
        if (!this.nextPageLink) {
            this.pagination.remove();
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
            if (this.paginationObserver) this.paginationObserver.disconnect();
        }
    }
}

customElements.define('infinite-collection', InfinteCollection);
